<script setup>
import { onMounted, ref } from "vue";
import Network from "@components/modals/Network.vue";
import { useBrandsStore } from "@composables/use_brands_store";
import {
  encriptar,
  desencriptar,
  encriptarCampo,
  siExistenDatosEncriptar,
} from "@composables/use_encript.js";


const { brands, phones, emails } = useBrandsStore();
var soloPirotecnia = ref(false);

const currentYear = new Date().getFullYear();

onMounted(() => {
  siExistenDatosEncriptar();
  const dominioURL = window.location.href;
  soloPirotecnia.value = dominioURL.includes("?pirotecnia");
});

function hasNetwork(network) {
  if (!brands.value?.length) return false;
  return brands.value.some((item) => {
    switch (network) {
      case "facebook":  return item.facebook_link  !== null;
      case "instagram": return item.instagram_link !== null;
      case "telegram":  return item.telegram_link  !== null;
      case "tikTok":    return item.tikTok_link    !== null;
      case "whatsapp":  return item.whatsApp_link  !== null;
      case "youtube":   return item.youtube_link   !== null;
      default:          return item.x_link         !== null;
    }
  });
}
</script>

<template>
  <footer class="footer-root">

    <!-- ── Main content ── -->
    <div class="footer-inner">

      <!-- Three-column grid -->
      <div class="footer-columns">

        <!-- Col 1 – Customer service -->
        <div class="footer-col">
          <h3 class="footer-col-title">Servicios al cliente</h3>
          <ul class="footer-links">
            <li>
              <router-link :to="{ name: soloPirotecnia ? 'AboutUsPirotecnia' : 'AboutUs' }" class="footer-link">
                Quiénes somos
              </router-link>
            </li>
            <li>
              <router-link :to="{ name: soloPirotecnia ? 'FrequentQuestionsPirotecnia' : 'FrequentQuestions' }" class="footer-link">
                Preguntas frecuentes
              </router-link>
            </li>
            <li>
              <router-link :to="{ name: soloPirotecnia ? 'TermsAndConditionsPirotecnia' : 'TermsAndConditions' }" class="footer-link">
                Términos y condiciones
              </router-link>
            </li>
            <li>
              <router-link :to="{ name: soloPirotecnia ? 'ShipmentsPirotecnia' : 'Shipments' }" class="footer-link">
                Envíos
              </router-link>
            </li>
            <li>
              <router-link :to="{ name: soloPirotecnia ? 'BranchesPirotecnia' : 'Branches' }" class="footer-link">
                Sucursales
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Col 2 – Contact info -->
        <div class="footer-col">
          <h3 class="footer-col-title">Contacto</h3>

          <div v-if="phones.length" class="footer-contact-group">
            <p class="footer-contact-label">Teléfonos</p>
            <ul class="footer-links">
              <li v-for="(phone, index) in phones" :key="`phone_${index}`">
                <a :href="`tel:${phone.phone}`" class="footer-link footer-link--icon">
                  <svg class="footer-link-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                  {{ phone.name }}: {{ phone.phone }}
                </a>
              </li>
            </ul>
          </div>

          <div v-if="emails.length" class="footer-contact-group">
            <p class="footer-contact-label">Emails</p>
            <ul class="footer-links">
              <li v-for="(email, index) in emails" :key="`email_${index}`">
                <a :href="`mailto:${email.email}`" target="_blank" class="footer-link footer-link--icon">
                  <svg class="footer-link-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                  {{ email.name }}: {{ email.email }}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Col 3 – Social networks -->
        <div class="footer-col">
          <h3 class="footer-col-title">Encuéntranos en</h3>
          <div class="footer-socials">
            <Network v-if="hasNetwork('facebook')"  :type="'facebook'"  :brands="brands" :up="true" id="link_facebook"  icon-class="w-9 h-9" />
            <Network v-if="hasNetwork('instagram')" :type="'instagram'" :brands="brands" :up="true" id="link_instagram" icon-class="w-9 h-9" />
            <Network v-if="hasNetwork('telegram')"  :type="'telegram'"  :brands="brands" :up="true" id="link_telegram"  icon-class="w-9 h-9" />
            <Network v-if="hasNetwork('tikTok')"    :type="'tikTok'"    :brands="brands" :up="true" id="link_tikTok"    icon-class="w-9 h-9" />
            <Network v-if="hasNetwork('whatsapp')"  :type="'whatsapp'"  :brands="brands" :up="true" id="link_whatsapp"  icon-class="w-9 h-9" />
            <Network v-if="hasNetwork('youtube')"   :type="'youtube'"   :brands="brands" :up="true" id="link_youtube"   icon-class="w-9 h-9" />
          </div>
        </div>

      </div>
    </div>

    <!-- ── Bottom bar ── -->
    <div class="footer-bottom">
      <p class="footer-copy">
        © {{ currentYear }} Baster Hermanos. Todos los derechos reservados.
      </p>
      <p class="footer-copy footer-copy--muted">
        Diseñado con ♥ en Argentina
      </p>
    </div>

  </footer>
</template>

<style scoped>
/* ── Root ── */
.footer-root {
  background: linear-gradient(160deg, #1a1f2e 0%, #111827 60%, #0f1117 100%);
  color: #cbd5e1;
  font-family: system-ui, -apple-system, sans-serif;
}

/* ── Inner wrapper ── */
.footer-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 48px 24px 32px;
}

/* ── Columns ── */
.footer-columns {
  display: grid;
  grid-template-columns: 1fr;
  gap: 36px;
}

@media (min-width: 640px) {
  .footer-columns { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 1024px) {
  .footer-columns { grid-template-columns: repeat(3, 1fr); }
}

/* ── Column heading ── */
.footer-col-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #e41b1b;
  margin: 0 0 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(228, 27, 27, 0.25);
}

/* ── Links ── */
.footer-links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-link {
  font-size: 13.5px;
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.15s, padding-left 0.15s;
  display: inline-block;
}

.footer-link:hover {
  color: #ffffff;
  padding-left: 4px;
}

.footer-link--icon {
  display: flex;
  align-items: center;
  gap: 7px;
}

.footer-link-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: #e41b1b;
}

/* ── Contact groups ── */
.footer-contact-group + .footer-contact-group { margin-top: 18px; }

.footer-contact-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin: 0 0 8px;
}

/* ── Social icons ── */
.footer-socials {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
}

/* ── Bottom bar ── */
.footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 16px 24px;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}

.footer-copy {
  font-size: 12px;
  color: #475569;
  margin: 0;
}

.footer-copy--muted { color: #334155; }
</style>
